import React, { useContext, useEffect, useState } from 'react'
import { Roles } from '../../UserContext'
import Table, { ColumnProps } from '../Common/Table'
import { displayCountdown, displayDate, displayAgeRange, displayDayList, displayZipCode } from '../../utilities'
import Responses from './Responses'
import { ServiceRequestContext } from './ServiceRequestContext'
import type { ServiceRequestService } from '../../models'
import ResponseCounts from '../Common/ResponseCounts'
import { Award, Reject } from '../Common/ResponseActionButtons'
import CommentCountWithIcon from './Comments/CommentCountWithIcon'
import { getResendServicePostingsAriaLabel, RESEND_SERVICE_POSTINGS_BUTTON_TEXT } from './constants'

const scheduleText = (row: ServiceRequestService): string => {
    const days = displayDayList(row.preferredDaysList || [])
    let text = `${days}, ${row.preferredTime || ''}; ${row.frequencyHoursPerVisit || ''}h`
    if (!(days).includes('Every') && row.frequencyVisitsTimeframe)
        text += `; Every ${row.frequencyVisitsTimeframe}`
    return text
}

const formatResendFailure = (failureReason?: string | null): string => {
    switch (failureReason) {
        case 'forbidden_role':
            return 'You are not authorized to resend this referral.'
        case 'forbidden_assignment_scope':
            return 'You can only resend referrals assigned to you.'
        case 'auth_start_in_past':
            return 'This referral cannot be resent because the authorization start date is in the past.'
        case 'auth_end_reached':
            return 'This referral cannot be resent because the authorization end date has been reached.'
        case 'no_eligible_providers':
            return 'No eligible providers were found. The referral validity was not changed.'
        case 'duplicate_in_flight':
            return 'A resend is already in progress for this referral. Please wait and try again.'
        case 'not_eligible_state':
            return 'Only expired, unassigned referrals can be resent.'
        default:
            return 'Unable to resend referral right now. Please try again.'
    }
}

const isAuthStartInPast = (startDate?: Date | string): boolean => {
    if (!startDate)
        return false

    const parsedStartDate = new Date(startDate)
    if (Number.isNaN(parsedStartDate.getTime()))
        return false

    return parsedStartDate.getTime() < Date.now()
}

const List: React.FC = () => {
    const { services, loading, error, onViewService, totalRows, filterValues, setFilterValues, isSearchOpportunities, refreshList, resendServiceReferral, resendingServiceID } = useContext(ServiceRequestContext)
    const [resendFeedback, setResendFeedback] = useState<string>()
    const [resendToastMessage, setResendToastMessage] = useState<string>()

    useEffect(() => {
        if (!resendToastMessage)
            return

        const dismissHandle = window.setTimeout(() => {
            setResendToastMessage(undefined)
        }, 5000)

        return () => {
            window.clearTimeout(dismissHandle)
        }
    }, [resendToastMessage])

    useEffect(() => {
        if (!resendFeedback)
            return

        const dismissHandle = window.setTimeout(() => {
            setResendFeedback(undefined)
        }, 5000)

        return () => {
            window.clearTimeout(dismissHandle)
        }
    }, [resendFeedback])

    const onResendReferral = async (service: ServiceRequestService) => {
        if (!service.serviceRequestServiceID)
            return

        setResendFeedback(undefined)

        const resendResult = await resendServiceReferral(service.serviceRequestServiceID)
        if (resendResult.success) {
            const recipients = resendResult.emails?.length ?? 0
            setResendFeedback(`Resend sent to ${recipients} provider recipient${recipients === 1 ? '' : 's'}.`)
            refreshList()
            return
        }

        const failureMessage = formatResendFailure(resendResult.failureReason)
        if (resendResult.failureReason === 'no_eligible_providers') {
            setResendFeedback(undefined)
            setResendToastMessage(failureMessage)
            return
        }

        setResendFeedback(failureMessage)
    }

    const noDataMessage = filterValues.filterGroup === 'ExpiredUnassigned'
        ? 'No expired unassigned postings match your current criteria.'
        : 'No Records Found'

    const columns: ColumnProps<ServiceRequestService> = [
        { title: 'Plan', dataIndex: 'member.plan', showForRoles: [Roles.Provider], sortable: true },
        {
            title: 'Type',
            dataIndex: 'specialtyServicesCategories',
            showForRoles: [Roles.Provider],
            sortable: true,
            render: (_, row) => {
                return row.specialtyServicesCategories || row.specialty;
            }
        },
        {
            title: 'Status',
            dataIndex: 'statusDisplay',
            hideForRoles: isSearchOpportunities ? undefined : [Roles.Provider],
            showForRoles: undefined,
            sortable: true
        },
        { title: 'ID', dataIndex: 'displayIdentifier', hideForRoles: [Roles.Provider], sortable: true },
        { title: 'CM', dataIndex: 'user.name', hideForRoles: [Roles.Provider], sortable: true },
        { title: 'Member ID', dataIndex: 'member.memberID', hideForRoles: [Roles.Provider], sortable: true },
        { title: 'County', dataIndex: 'member.county', sortable: true },
        {
            title: 'Service Category',
            dataIndex: 'specialtyServicesCategories',
            hideForRoles: [Roles.Provider],
            sortable: true,
            render: (_, row) => {
                return row.specialtyServicesCategories || row.specialty;
            }
        },
        {
            title: 'Zip Code',
            dataIndex: 'member.zipCode',
            sortable: true,
            showForRoles: [Roles.Provider],
            render: (zip: string | undefined) => displayZipCode(zip)
        },
        { title: 'Auth Start', dataIndex: 'startDate', render: displayDate, sortable: true },
        { title: 'Auth End', dataIndex: 'endDate', render: displayDate, sortable: true },
        { title: 'Total Units', dataIndex: 'totalUnits', showForRoles: [Roles.Provider] },
        {
            title: 'Auth Info', 
            dataIndex: 'additionalInfo', 
            showForRoles: isSearchOpportunities ? undefined : [Roles.Provider],
            render: (_, s) => (<div className='total-units' title={s.additionalInfo}>{s.additionalInfo}</div>)
        },
        ...(!isSearchOpportunities ? [
            { title: 'Comments', dataIndex: 'totalCommentCount', sortable: false, render: (_: unknown, r: ServiceRequestService) => <CommentCountWithIcon totalCommentCount={r.totalCommentCount || 0} hasUnreadComment={r.unreadCommentCount != null && r.unreadCommentCount > 0}/>}
        ] : []),
        ...(!isSearchOpportunities ? [
            {
                title: 'Respond',
                dataIndex: 'responseStatus',
                className: 'response-actions-container',
                showForRoles: [Roles.Provider],
                ignoreClick: true,
                render: (_: unknown, s: ServiceRequestService) => !s.isAwarded && (!s.decisionStatus || s.decisionStatus === 'NoDecision') && <Responses service={s} />
            }
        ] : []),
        ...(!isSearchOpportunities && filterValues.filterGroup === 'ExpiredUnassigned' ? [
            {
                title: 'Resend Referral',
                dataIndex: '',
                ignoreClick: true,
                render: (_: unknown, row: ServiceRequestService) => {
                    if (!row.serviceRequestServiceID)
                        return <p></p>

                    if (isAuthStartInPast(row.startDate))
                        return <span className='resend-referral-unavailable'>Auth start date is in the past. Resend unavailable.</span>

                    const isResending = resendingServiceID === row.serviceRequestServiceID
                    const serviceIdentifier = row.displayIdentifier || row.authorizationID || row.serviceRequestServiceID

                    return <button
                        type='button'
                        className='send-button resend-referral-action'
                        aria-label={getResendServicePostingsAriaLabel(serviceIdentifier)}
                        disabled={isResending}
                        onClick={() => { void onResendReferral(row) }}
                    >
                        {isResending
                            ? <div className='no-records send-loading'>
                                <img className='loading-img' src='/spinning-circles.svg' alt='loading' />
                            </div>
                            : RESEND_SERVICE_POSTINGS_BUTTON_TEXT}
                    </button>
                }
            }
        ] : []),
        ...(!isSearchOpportunities && filterValues.filterGroup !== 'ExpiredUnassigned' ? [
            {
                title: 'Awarded',
                dataIndex: '',
                render: (_: unknown, row: ServiceRequestService) => <>
                    { (row.serviceRequestServiceID !== undefined && (row.isAwarded || row.decisionStatus === 'Approved')) && <Award onClick={() => { return false }} title='Awarded' active={true} />}
                    { (row.serviceRequestServiceID !== undefined && (row.decisionStatus === 'Rejected')) && <Reject onClick={() => { return false }} title='Rejected' active={true} />}
                    { (row.serviceRequestServiceID === undefined) && <p></p>}
                </>
            }
        ] : [])
    ]

    return <div className={isSearchOpportunities ? 'search-opportunities-results' : ''}>
        {resendToastMessage && <div className='service-resend-toast' role='alert' aria-live='assertive'>{resendToastMessage}</div>}
        {resendFeedback && <div className='service-resend-feedback' role='status' aria-live='polite'>{resendFeedback}</div>}
        <Table
            data={services}
            columns={columns}
            loading={loading}
            error={!!error}
            noDataMessage={noDataMessage}
            onRowClick={onViewService}
            totalRows={totalRows}
            filters={filterValues}
            setFilters={setFilterValues}
            disableMultiColumnSort
        />
    </div>
}

export default List
